import { Drawer } from 'vaul';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useApprovalDetail } from '@/hooks/useApprovalDetail';
import { useResolveApproval } from '@/hooks/useApprovals';

interface Props {
  taskId: string | null;
  onClose: () => void;
  /** True when opened from /approvals (worker can approve/reject here). */
  allowActions?: boolean;
}

function money(n: number | string | null | undefined): string {
  if (n === null || n === undefined || n === '') return '—';
  const num = typeof n === 'number' ? n : Number(n);
  return Number.isFinite(num) ? num.toLocaleString() : String(n);
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ApprovalDetailDrawer({ taskId, onClose, allowActions = false }: Props) {
  const { t } = useTranslation();
  const { data, isLoading } = useApprovalDetail(taskId);
  const resolve = useResolveApproval();

  function onAction(action: 'approved' | 'rejected') {
    if (!taskId) return;
    let comment: string | undefined;
    if (action === 'rejected') {
      const input = window.prompt(t('approvals.prompt_reject_reason'));
      if (input === null) return;
      comment = input.trim() || t('approvals.default_reject_reason');
    }
    resolve.mutate(
      comment !== undefined ? { id: taskId, action, comment } : { id: taskId, action },
      {
        onSuccess: () => {
          toast.success(
            action === 'approved' ? t('approvals.toast_approved') : t('approvals.toast_rejected'),
          );
          onClose();
        },
        onError: () => toast.error(t('approvals.toast_error')),
      },
    );
  }

  return (
    <Drawer.Root open={Boolean(taskId)} onOpenChange={(open) => !open && onClose()}>
      <Drawer.Portal>
        <Drawer.Overlay className="appr-drawer-overlay" />
        <Drawer.Content className="appr-drawer">
          <Drawer.Title className="sr-only">{t('approvals.detail_title')}</Drawer.Title>
          <div className="appr-drawer-handle" />
          {isLoading || !data ? (
            <div className="appr-drawer-body" aria-busy="true">
              <div className="appr-skeleton" />
            </div>
          ) : (
            <div className="appr-drawer-body">
              <header className="appr-drawer-head">
                <div className="appr-drawer-num">{data.taskNumber}</div>
                <div className="appr-drawer-title-text">{data.title}</div>
                <div className="appr-drawer-sub">
                  {data.applicantName}{data.departmentName ? ` · ${data.departmentName}` : ''} · {formatDateTime(data.createdAt)}
                </div>
              </header>

              {data.summary && (
                <div className="appr-drawer-section">
                  <div className="appr-drawer-section-title">{t('approvals.detail_summary')}</div>
                  <div className="appr-drawer-body-text">{data.summary}</div>
                </div>
              )}

              <div className="appr-drawer-grid">
                <div className="appr-drawer-field">
                  <div className="appr-drawer-field-label">{t('approvals.detail_amount')}</div>
                  <div className="appr-drawer-field-value mono">{money(data.amount)}</div>
                </div>
                <div className="appr-drawer-field">
                  <div className="appr-drawer-field-label">{t('approvals.detail_status')}</div>
                  <div className="appr-drawer-field-value">
                    <span className="appr-status-chip" data-status={data.status}>
                      {t(`approvals.status_${data.status.toLowerCase()}`, { defaultValue: data.status })}
                    </span>
                  </div>
                </div>
                {data.bizNumber && (
                  <div className="appr-drawer-field">
                    <div className="appr-drawer-field-label">{t('approvals.detail_biz_number')}</div>
                    <div className="appr-drawer-field-value mono">{data.bizNumber}</div>
                  </div>
                )}
              </div>

              {/* Flow progress */}
              <div className="appr-drawer-section">
                <div className="appr-drawer-section-title">{t('approvals.detail_flow')}</div>
                <ol className="appr-flow-list">
                  {data.flowSnapshot.map((node, i) => {
                    const idx = i + 1;
                    const state = idx < data.currentNode ? 'done' : idx === data.currentNode ? 'active' : 'pending';
                    return (
                      <li key={node.order} className="appr-flow-node" data-state={state}>
                        <div className="appr-flow-bullet">{node.order}</div>
                        <div className="appr-flow-meta">
                          <div className="appr-flow-name">{node.name}</div>
                          {node.approverValue && (
                            <div className="appr-flow-approver">{node.approverValue}</div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>

              {/* Records */}
              {data.records.length > 0 && (
                <div className="appr-drawer-section">
                  <div className="appr-drawer-section-title">{t('approvals.detail_records')}</div>
                  <ul className="appr-records-list">
                    {data.records.map((r) => (
                      <li key={r.id} className="appr-record-row" data-action={r.action.toLowerCase()}>
                        <div className="appr-record-head">
                          <span className="appr-record-approver">{r.approverName}</span>
                          <span className="appr-record-action">
                            {t(`approvals.action_${r.action.toLowerCase()}`, { defaultValue: r.action })}
                          </span>
                          <span className="appr-record-time">{formatDateTime(r.createdAt)}</span>
                        </div>
                        <div className="appr-record-node">{r.nodeName}</div>
                        {r.comment && <div className="appr-record-comment">{r.comment}</div>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {allowActions && data.status === 'IN_PROGRESS' && (
                <div className="appr-drawer-actions">
                  <button
                    type="button"
                    className="appr-btn"
                    data-variant="reject"
                    disabled={resolve.isPending}
                    onClick={() => onAction('rejected')}
                  >
                    <span className="material-symbols-outlined">cancel</span>
                    {t('approvals.reject')}
                  </button>
                  <button
                    type="button"
                    className="appr-btn"
                    data-variant="approve"
                    disabled={resolve.isPending}
                    onClick={() => onAction('approved')}
                  >
                    <span className="material-symbols-outlined">check_circle</span>
                    {t('approvals.approve')}
                  </button>
                </div>
              )}
            </div>
          )}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
